import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Crown, CreditCard, Receipt, AlertCircle, ArrowUpCircle, Info, Building2, Users, DollarSign } from 'lucide-react';
import { BillingAdminAssignment } from './BillingAdminAssignment';
import { UpgradeRequestsList } from './UpgradeRequestsList';
import { BillingDashboard } from './BillingDashboard';
import { SubscriptionOverviewCard } from './SubscriptionOverviewCard';
import { isBillingAdmin as checkIsBillingAdmin } from '../utils/billingAdmin';
import { isFirmAdminUser } from '../utils/workspaceRouter';
import type { User, SubscriptionPlan, ConsultingFirm } from '../types';

interface BillingAdminDashboardProps {
  currentUser: User;
  firm: ConsultingFirm;
  allUsers: User[];
  accessToken: string;
  onDataChanged?: () => void;
}

export function BillingAdminDashboard({
  currentUser,
  firm,
  allUsers,
  accessToken,
  onDataChanged,
}: BillingAdminDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    pendingRequests: 0,
    monthlySpend: 0,
    activeUsers: 0,
    activeCompanies: 0,
  });

  const isBillingAdmin = checkIsBillingAdmin(currentUser);
  // Firm admins change the plan and sync seats too (the server's canManageBilling); approvals stay with the Billing Admin
  const canManagePlan = isBillingAdmin || isFirmAdminUser(currentUser);

  useEffect(() => {
    loadStats();
  }, [firm?.id, allUsers?.length]); // Reload when firm or users change

  const loadStats = async () => {
    // Load actual stats from the backend
    const firmId = firm?.id || firm?.consultingFirmId || currentUser?.consultingFirmId;
    
    console.log('[BillingAdminDashboard] Loading stats for firm:', firmId);
    console.log('[BillingAdminDashboard] Total users:', allUsers.length);
    
    // Count active firm users (consultants, CF admins, billing admins in this firm)
    const activeFirmUsers = allUsers.filter(u => {
      if (u.status !== 'active') return false;
      // Must belong to this consulting firm
      const userFirmId = u.consultingFirmId || u.firmId;
      if (userFirmId !== firmId) return false;
      // Include CF Admins, Consultants, Billing Admins (exclude company users)
      const firmRoles = ['cfAdmin', 'consultant', 'billingAdmin', 'bdRep', 'consulting_firm_admin', 'billing_admin', 'bd_rep'];
      const hasRole = u.roles && Array.isArray(u.roles) 
        ? u.roles.some((r: string) => firmRoles.includes(r))
        : firmRoles.includes(u.role);
      return hasRole;
    }).length;
    
    console.log('[BillingAdminDashboard] Active firm users:', activeFirmUsers);
    
    setStats({
      pendingRequests: 0, // Would fetch from upgrade requests API
      monthlySpend: getPlanPrice(firm?.subscriptionPlan),
      activeUsers: activeFirmUsers,
      activeCompanies: 0, // Would fetch from companies belonging to this firm
    });
  };

  const getPlanPrice = (plan: SubscriptionPlan): number => {
    const prices: Record<SubscriptionPlan, number> = {
      free: 0,
      solo: 299,
      starter: 499,
      premium: 1299,
      enterprise: 4999,
    };
    return prices[plan] || 0;
  };

  const getPlanName = (plan: SubscriptionPlan): string => {
    if (!plan) return 'Free'; // Handle undefined/null plan
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-600">Active</Badge>;
      case 'trial':
        return <Badge variant="secondary">Trial</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="outline">Canceled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
            <Crown className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div>
            <h1 className="text-2xl">Billing & Subscription</h1>
            <p className="text-muted-foreground">
              Manage your subscription, billing, and payment settings
            </p>
          </div>
        </div>

        {!isBillingAdmin && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {canManagePlan
                ? 'As Firm Admin you can change the plan and sync the user count. Approving upgrade requests and managing payment methods stay with the Billing Admin.'
                : 'You are viewing billing information in read-only mode. Only the Billing Admin can make changes to the subscription, approve upgrades, or manage payment methods.'}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Current Plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-bold">{getPlanName(firm.subscriptionPlan)}</span>
              </div>
              {firm.subscriptionStatus && getStatusBadge(firm.subscriptionStatus)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Monthly Spend</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">${stats.monthlySpend}</span>
            </div>
            <p className="text-muted-foreground">/month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{stats.activeUsers}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Pending Requests</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{stats.pendingRequests}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Upgrade requests awaiting approval
            </p>
            {stats.pendingRequests > 0 && isBillingAdmin && (
              <Badge variant="destructive" className="mt-2">Needs Review</Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="billing-admin" className="gap-2">
            <Crown className="h-4 w-4" />
            Billing Admin
          </TabsTrigger>
          <TabsTrigger value="upgrade-requests" className="gap-2">
            <ArrowUpCircle className="h-4 w-4" />
            Upgrade Requests
            {stats.pendingRequests > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.pendingRequests}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2">
            <Receipt className="h-4 w-4" />
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <SubscriptionOverviewCard accessToken={accessToken} firmId={firm.id} canManage={canManagePlan} />
          <BillingDashboard accessToken={accessToken} firmId={firm.id} />
        </TabsContent>

        {/* Billing Admin Tab */}
        <TabsContent value="billing-admin" className="space-y-4">
          <BillingAdminAssignment
            currentUser={currentUser}
            firmId={firm.id}
            firmPlan={firm.subscriptionPlan}
            allUsers={allUsers}
            accessToken={accessToken}
            onAssignmentChanged={() => {
              if (onDataChanged) onDataChanged();
            }}
          />

          {isBillingAdmin && (
            <Alert>
              <Crown className="h-4 w-4 text-yellow-500" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium">You are the Billing Admin</p>
                  <p className="text-muted-foreground">
                    As Billing Admin, you have exclusive access to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Approve or deny subscription upgrade requests from team members</li>
                    <li>Update payment methods and billing information</li>
                    <li>Purchase additional company slot addons</li>
                    <li>View and download invoices</li>
                    <li>Cancel or downgrade the subscription</li>
                    <li>Reassign the Billing Admin role to another team member</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Upgrade Requests Tab */}
        <TabsContent value="upgrade-requests" className="space-y-4">
          <UpgradeRequestsList
            firmId={firm?.id || firm?.consultingFirmId || currentUser?.consultingFirmId}
            currentUser={currentUser}
            accessToken={accessToken}
            autoRefresh={isBillingAdmin}
          />
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoice History
              </CardTitle>
              <CardDescription>
                View and download your billing invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isBillingAdmin ? (
                <p className="text-muted-foreground text-center py-8">
                  Invoice history will be displayed here. Integration with Stripe Customer Portal coming soon.
                </p>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Only the Billing Admin can view and download invoices. Contact your Billing Admin if you need access to billing documents.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}